/* The fog of war, applied by the client to a snapshot it holds in full.

   The replay is served unredacted — the war is over — and the owner wants to watch it *as a seat
   saw it* (2026-09-06): "even if the server does not have that disabled, in the replays we should
   be able to toggle it". So this is the server's `visible_cell_contents` and `_redact_metadata`,
   in the client's language, run over a step's metadata for one seat. It mirrors, it does not
   decide: the live game is still fogged by the server, and a client that disagreed with it here
   would only mislead a replay.

   The rules, in the order the server states them (requirement 09, T2):
   - a cell's contents are readable when the seat's team holds it, has troops standing on it, has
     a march standing on it, or when it lies within the
     detection radius of any of those — the ring the map draws, each ring measured with its own
     owner's research (U3);
   - a movement is readable when it is the team's own, the viewer is in its `revealed_to`, or it
     stands still on a square the viewer can read, and comes with its composition (36/V1, owner
     2026-10-26); an order given this round is folded back into its garrison or frozen;
   - an event is readable when the viewer is in its `visible_to`; the battle plan is the team's;
     a battle's running bill is the fighters';
   - an enemy's `users` entry keeps its public fields only (side, class, starting cities, stance,
     when it fell) — its food, research and searches are its own;
   - the army census is recomputed from what survives, since it would otherwise leak the rest.

   Two callers: the replay's fog eye (`hooks/useReplay`), and a live spectator "watching as" a
   seat (`selectGame`, owner 2026-09-29), whose "Spectator" choice is the board without it. */

import { alliesOf, hasUnits, teamLookouts } from "src/utils/lookouts"
import { activeWorld } from "src/utils/world"

// What an enemy's entry in `users` may say — `games/visibility.PUBLIC_USER_FIELDS`, word for word.
const PUBLIC_USER_FIELDS = [
  "team",
  "race",
  "initial_location_list",
  "starting_location_list",
  "crowns_lost",
  "bot_mode",
  "eliminated_at_tick",
]

/* Every square the team looks out from and how far each sees — `utils/lookouts`. */
const visionSources = teamLookouts

/* Every square within each source's own radius of it, the short way round the world. */
const within = (sources) => {
  const { columns: COLUMNS, rows: ROWS } = activeWorld()
  const seen = new Set()
  sources.forEach((source) => {
    const radius = source.radius
    if (radius < 0) return
    for (let dCol = -radius; dCol <= radius; dCol += 1) {
      const span = radius - Math.abs(dCol)
      const col = (((source.col + dCol) % COLUMNS) + COLUMNS) % COLUMNS
      for (let dRow = -span; dRow <= span; dRow += 1) {
        const row = source.row + dRow
        if (row >= 0 && row < ROWS) seen.add(`${col}-${row}`)
      }
    }
  })
  return seen
}

/* Every square the viewer's team currently looks over — land and sea — as the union of the rings
   around its cities, camps and marches, each with its owner's reach. This is what the map paints
   as "already seen" when a cell is pressed (U3), so the ring around the pressed cell is read
   against what the team already has rather than on its own. Mirrors the server's `team_vision`:
   `visibleCellKeys` below is the same set narrowed to squares that exist as cells plus the
   per-cell rules, so the picture and the redaction cannot disagree. */
export const teamVisionKeys = (metadata, viewerId, rules) => {
  const users = metadata?.users || {}
  if (!users[String(viewerId)]) return new Set()
  return within(visionSources(metadata, alliesOf(users, viewerId), rules))
}

/* The cells whose contents `viewerId` may read in this snapshot. */
export const visibleCellKeys = (metadata, viewerId, rules) => {
  const users = metadata?.users || {}
  const viewer = users[String(viewerId)]
  if (!viewer) return new Set()
  const allies = alliesOf(users, viewerId)
  const visible = new Set()

  Object.entries(metadata.cells || {}).forEach(([key, cell]) => {
    const held = cell?.owner != null && allies.has(String(cell.owner))
    const camped = Object.entries(cell?.metadata_by_user || {}).some(
      ([id, entry]) => allies.has(String(id)) && hasUnits(entry?.units)
    )
    if (held || camped) visible.add(key)
  })
  ;(metadata.movements || []).forEach((movement) => {
    if (!allies.has(String(movement.user_id))) return
    const position = movement.path?.[movement.current_index ?? 0]
    if (position) visible.add(`${position[0]}-${position[1]}`)
  })
  within(visionSources(metadata, allies, rules)).forEach((key) => {
    if (metadata.cells?.[key]) visible.add(key)
  })
  return visible
}

const census = (metadata) => {
  const totals = {}
  Object.values(metadata.cells || {}).forEach((cell) => {
    Object.entries(cell?.metadata_by_user || {}).forEach(([id, entry]) => {
      const row = (totals[String(id)] = totals[String(id)] || {})
      Object.entries(entry?.units || {}).forEach(([unit, data]) => {
        row[unit] = (row[unit] || 0) + (data?.number ?? 0)
      })
    })
  })
  ;(metadata.movements || []).forEach((movement) => {
    const row = (totals[String(movement.user_id)] = totals[String(movement.user_id)] || {})
    Object.entries(movement.units || {}).forEach(([unit, data]) => {
      row[unit] = (row[unit] || 0) + (data?.number ?? 0)
    })
  })
  // The tunnel network (2026-09-29) counts too — `games/visibility._census`, word for word. The
  // users here are already redacted, so an enemy's entry carries no `tunnel` and adds nothing.
  Object.entries(metadata.users || {}).forEach(([id, entry]) => {
    Object.entries(entry?.tunnel || {}).forEach(([unit, data]) => {
      const row = (totals[String(id)] = totals[String(id)] || {})
      row[unit] = (row[unit] || 0) + (data?.number ?? 0)
    })
  })
  return totals
}

/* The snapshot as `viewerId` would have received it. A new object; the input is left whole.

   `past`: the board is a round already played — a replay step, or a past round of a live game —
   mirroring `games/visibility.filter_step_metadata_for_user` (owner, 2026-10-05). An enemy order
   of that round is then not withheld: from a start cell the viewer could see it shows its road as
   far as their sight reached, cut at the first square they could not read; from one they could
   not see it stays hidden. */
/* The round's motion log as one side may see it, mirroring `zvh/games/round_trail.redact_motion`
   (requirement 36, A6, V2, P2): the side's own stacks and steps whole; an enemy `move` where its `to`
   is in sight, marked `from_out_of_sight` when its `from` was not; a contact, battle, capture or
   landing where the cell is in sight or the side is a party; a strike the firer's side's, else in
   sight; a tunnel order or a retirement the orderer's side's only; a deed leaving for the loser's
   side, whoever saw the cell, and the side that took it. An enemy stack keeps its units, and its
   `start` only in sight; a garrison is kept where its battle is. "In sight" is the side's sight
   when the step happened (owner, 2026-10-27, 36/A9): the `seen_by` the round wrote on it
   (`from_seen_by` for a move's `from`, `start_seen_by` for a stack's `start`), and none of those
   marks is handed on. A log written before them is read with `seen`, a Set of "col-row" keys: the
   side's readable cells and vision at the end of the round (A6). Null when nothing is the side's. */
const SIGHT_MARKS = ["seen_by", "from_seen_by", "start_seen_by"]
const unmarked = (entry) =>
  SIGHT_MARKS.some((mark) => mark in (entry || {}))
    ? Object.fromEntries(Object.entries(entry).filter(([key]) => !SIGHT_MARKS.includes(key)))
    : entry
const logKey = (cell) => (Array.isArray(cell) ? `${cell[0]}-${cell[1]}` : cell ? `${cell.column}-${cell.row}` : "")

export const redactMotion = (motion, { team, users, seen }) => {
  if (!motion?.steps?.length) return null
  const stacks = motion.stacks || {}
  const allies = new Set(Object.entries(users || {}).filter(([, entry]) => entry?.team === team).map(([id]) => String(id)))
  const ownIds = new Set(Object.entries(stacks).filter(([, stack]) => allies.has(String(stack?.user_id))).map(([id]) => id))
  const isAlly = (userId) => allies.has(String(userId))
  const inSight = (cell) => seen.has(logKey(cell))
  // By the mark the round wrote (A9), else the sight at the end of the round (A6).
  const saw = (marked, mark, cell) => (mark in (marked || {}) ? (marked[mark] || []).includes(team) : inSight(cell))
  const keptSteps = []
  const named = new Set()
  motion.steps.forEach((step) => {
    const kind = step?.kind
    let kept = step
    if (kind === "move") {
      if (!ownIds.has(step.id)) {
        if (!saw(step, "seen_by", step.to)) return
        if (!saw(step, "from_seen_by", step.from)) kept = { ...step, from_out_of_sight: true }
      }
      named.add(kept.id)
    } else if (kind === "contact") {
      const ids = step.ids || []
      if (!saw(step, "seen_by", step.at) && !ids.some((id) => ownIds.has(id))) return
      ids.forEach((id) => named.add(id))
    } else if (kind === "battle") {
      if (!saw(step, "seen_by", step.at) && !(step.teams || []).includes(team)) return
    } else if (kind === "taken") {
      if (!saw(step, "seen_by", step.at) && !ownIds.has(step.id) && !isAlly(step.from_owner)) return
      named.add(step.id)
    } else if (kind === "land") {
      if (!saw(step, "seen_by", step.at) && !ownIds.has(step.id)) return
      named.add(step.id)
    } else if (kind === "strike") {
      if (!isAlly(step.by) && !saw(step, "seen_by", step.at)) return
    } else if (kind === "tunnel" || kind === "discharge") {
      if (!isAlly(step.by)) return
    } else if (kind === "lost") {
      const here = logKey(step.at)
      const tookIt = motion.steps.some(
        (other) =>
          ((other.kind === "taken" && ownIds.has(other.id)) || (other.kind === "battle" && (other.teams || []).includes(team))) &&
          logKey(other.at) === here
      )
      if (!saw(step, "seen_by", step.at) && !isAlly(step.from_owner) && !tookIt) return
    } else {
      return
    }
    // Who else saw it is not the side's to know (A9).
    keptSteps.push(unmarked(kept))
  })
  if (!keptSteps.length) return null
  const fought = new Set(keptSteps.filter((step) => step.kind === "battle").map((step) => logKey(step.at)))
  const stepped = new Set(motion.steps.filter((step) => step?.kind === "move").map((step) => step.id))
  const keptStacks = {}
  Object.entries(stacks).forEach(([id, stack]) => {
    if (ownIds.has(id)) {
      keptStacks[id] = unmarked(stack)
    } else if (stack?.garrison) {
      if (fought.has(logKey(stack.start))) keptStacks[id] = unmarked(stack)
    } else {
      // A stack held on a fought site with no step this round (a besieger) is kept where its
      // battle is, start and all, as a garrison is (36/B5).
      const besieger = !stepped.has(id) && fought.has(logKey(stack?.start))
      if (!named.has(id) && !besieger) return
      const { start, ...rest } = unmarked(stack)
      keptStacks[id] = start != null && (besieger || saw(stack, "start_seen_by", start)) ? { ...rest, start } : rest
    }
  })
  return { ...(motion.tick != null ? { tick: motion.tick } : {}), stacks: keptStacks, steps: keptSteps }
}

export const redactForViewer = (metadata, viewerId, rules, { past = false } = {}) => {
  if (!metadata) return metadata
  const tick = metadata.tick
  const users = metadata.users || {}
  const viewer = users[String(viewerId)]
  if (!viewer) return metadata
  const allies = alliesOf(users, viewerId)
  const visible = visibleCellKeys({ ...metadata, tick }, viewerId, rules)

  /* A colony is not on the map until somebody has looked at it (owner, 2026-09-09), mirroring
     `games/visibility._redact_metadata`. The three cities a player starts on stay public — that is
     what Notion's V1 was about, and a crown badge needs a cell under it — and discovery is sticky
     (`discovered_by`, written server-side by `compute_visibility`), so a colony scouted once does
     not vanish again when the scout walks home. Everything else the viewer has never seen is not
     in this snapshot's map at all. */
  // All three starting cities (`starting_location_list`, requirement 35 M4) — on S and M a player
  // has fewer crowns than starting cities, and every one of the three stays public. A snapshot
  // from before the sizes has only `initial_location_list`, which was all three.
  const startingCities = new Set(
    Object.values(users).flatMap((user) =>
      (user?.starting_location_list || user?.initial_location_list || []).map(([column, row]) => `${column}-${row}`)
    )
  )
  const seenSquares = new Set([...visible, ...teamVisionKeys(metadata, viewerId, rules)])
  const cells = {}
  Object.entries(metadata.cells || {}).forEach(([key, cell]) => {
    if (visible.has(key)) {
      cells[key] = cell
      return
    }
    const known =
      startingCities.has(key) || seenSquares.has(key) || (cell?.discovered_by || []).map(Number).includes(Number(viewerId))
    if (known) cells[key] = { ...cell, metadata_by_user: {} }
  })

  // Which movements a viewer sees, mirroring `games/visibility._redact_movements`: a stack standing
  // still on a square the viewer can read — held at the gates, arrived, frozen by a withheld order —
  // and a march the viewer has spotted. Every one of them comes with its units (36/V1). "Can read"
  // is the readable cells plus the team's whole vision, bare ground included.
  const seen = seenSquares
  // An order given on the board's tick has not walked yet: it stands on its origin, as
  // `games/visibility._standing` has it.
  const standing = (movement) => {
    const path = movement.path || []
    return (
      Boolean(movement.halted) ||
      Boolean(movement.has_arrived) ||
      (movement.current_index ?? 0) >= path.length - 1 ||
      (tick != null && (movement.created_tick ?? 0) >= tick)
    )
  }
  const standsAt = (movement) => {
    const path = movement.path || []
    const cell = path[Math.min(movement.current_index ?? 0, path.length - 1)]
    return cell ? `${cell[0]}-${cell[1]}` : null
  }
  // An order placed this round is withheld from the enemy until the round resolves (Q26) — but
  // the stack it moves does not vanish (owner, 2026-09-07): folded back into the garrison it
  // left where the viewer can read that garrison, so the cell reads as it did before the order;
  // frozen where it stands, one-cell path and no route, when there is no garrison to fold into.
  // Neither this nor the standing rule waits on `revealed_to` (game 3316): a fresh order is in
  // nobody's list. The list is what keeps a march on the move in view once spotted. Mirrors
  // `games/visibility._redact_metadata`.
  const originKey = (movement) => {
    const cell = (movement.path || [])[0]
    return cell ? `${cell[0]}-${cell[1]}` : null
  }
  const frozen = (movement) => ({
    ...movement,
    type: "move",
    path: (movement.path || []).slice(0, 1),
    current_index: 0,
    ticks_remaining: 0,
    halted: false,
    has_arrived: true,
    came_from: null,
    walked: [],
  })
  const foldIntoGarrison = (movement, origin) => {
    if (!visible.has(origin)) return false
    const owner = String(movement.user_id)
    const garrison = cells[origin]?.metadata_by_user?.[owner]
    if (!garrison) return false
    const units = { ...(garrison.units || {}) }
    Object.entries(movement.units || {}).forEach(([unit, data]) => {
      const number = data?.number ?? 0
      if (number) units[unit] = { ...(units[unit] || {}), number: (units[unit]?.number ?? 0) + number }
    })
    cells[origin] = { ...cells[origin], metadata_by_user: { ...cells[origin].metadata_by_user, [owner]: { ...garrison, units } } }
    return true
  }
  const bodiesOf = (movement) =>
    Object.values(movement.units || {}).reduce((sum, unit) => sum + (unit?.number ?? 0), 0)
  const revealedTo = (movement) => (movement.revealed_to || []).map(String).includes(String(viewerId))
  const cutToSight = (movement) => {
    const path = movement.path || []
    const end = path.findIndex(([column, row]) => !seen.has(`${column}-${row}`))
    if (end === -1) return movement
    return { ...movement, path: end === 0 ? path.slice(0, 1) : path.slice(0, end), ticks_remaining: 0, out_of_sight: true }
  }
  // Ordered out of the fog (`_enter_from_fog`, owner 2026-10-05): the part of the road that came
  // into sight and the fog square it stepped out of, with its units (36/V1).
  const enterFromFog = (movement) => {
    const path = movement.path || []
    const inSight = ([column, row]) => seen.has(`${column}-${row}`)
    const first = path.findIndex((cell, index) => index > 0 && inSight(cell))
    if (first === -1) return []
    let last = first
    while (last + 1 < path.length && inSight(path[last + 1])) last += 1
    const entering = {
      ...movement,
      path: path.slice(first, last + 1),
      current_index: 0,
      ticks_remaining: 0,
      came_from: null,
      walked: [],
      entered_from: path[first - 1],
      from_out_of_sight: true,
    }
    if (last + 1 < path.length) entering.out_of_sight = true
    return [entering]
  }
  // The road an enemy stack in a fight walked this round, as far back as the viewer saw it
  // (owner, 2026-10-08): the server's `_walked_in_sight`. Out of the fog, the fog square it
  // stepped out of rides as `walked_entered_from` and the map fades the line in from it.
  const walkedInSight = (movement) => {
    const walked = Array.isArray(movement.walked) ? movement.walked : []
    let lastHidden = -1
    walked.forEach(([column, row], index) => {
      if (!seen.has(`${column}-${row}`)) lastHidden = index
    })
    if (lastHidden < 0) return movement
    return { ...movement, walked: walked.slice(lastHidden + 1), walked_entered_from: walked[lastHidden] }
  }
  const movements = (metadata.movements || [])
    .flatMap((movement) => {
      if (allies.has(String(movement.user_id))) return [movement]
      const withheld = tick != null && (movement.created_tick ?? 0) >= tick
      if (withheld && past) return seen.has(originKey(movement)) ? [cutToSight(movement)] : enterFromFog(movement)
      if (withheld) {
        const origin = originKey(movement)
        if (!seen.has(origin)) return []
        // A stack of nothing is nothing; one with bodies folds into the garrison it left, or is
        // frozen where it stands when there is none to fold into.
        if (bodiesOf(movement) > 0) return foldIntoGarrison(movement, origin) ? [] : [frozen(movement)]
        return []
      }
      const standingInView = standing(movement) && seen.has(standsAt(movement))
      return revealedTo(movement) || standingInView ? [movement] : []
    })
    .map((movement) => (allies.has(String(movement.user_id)) ? movement : walkedInSight(movement)))

  const events = (metadata.events || []).filter((event) =>
    (event.visible_to || []).map(String).includes(String(viewerId))
  )
  const formations = Object.fromEntries(
    Object.entries(metadata.formations || {}).filter(([team]) => team === viewer.team)
  )
  // A missile in the air is the firer's team's until it lands (owner, 2026-10-02), as the server
  // has it (`_redact_metadata`): the enemy reads it in the next round's report.
  const strikes = (metadata.strikes || []).filter((strike) => allies.has(String(strike.user_id)))
  // So is a building level or a colony ordered this round (owner, 2026-10-02): the enemy sees the
  // level or the city when the round turns.
  const upgrades = (metadata.upgrades || []).filter((order) => allies.has(String(order.user_id)))
  const foundings = (metadata.foundings || []).filter((order) => allies.has(String(order.user_id)))
  // And a tunnel entry or exit (owner, 2026-10-05): the garrison changes when the round turns.
  const tunnelOrders = (metadata.tunnel_orders || []).filter((order) => allies.has(String(order.user_id)))
  // And a discharge (owner, 2026-10-23): the enemy sees the workers when they land.
  const discharges = (metadata.discharges || []).filter((order) => allies.has(String(order.user_id)))
  const battleTotals = Object.fromEntries(
    // The team's, not the viewer's alone, as the server has it (`_redact_metadata`): a 2v2
    // partner reads the bill of an ally's fight, which is on a cell the team already sees.
    Object.entries(metadata.battle_totals || {}).filter(([, totals]) =>
      Object.keys(totals || {}).some((id) => allies.has(String(id)))
    )
  )

  // The meat tally is the same bill's other half (U5): readable at the sites the seat is fighting.
  const battleMeat = Object.fromEntries(
    Object.entries(metadata.battle_meat || {}).filter(([site]) => site in battleTotals)
  )

  // And who the medics got back on their feet, the bill's third figure: same sites. (It counted
  // health points until 2026-09-14, when the medic stopped healing and started reviving.)
  const battleRevived = Object.fromEntries(
    Object.entries(metadata.battle_revived || {}).filter(([site]) => site in battleTotals)
  )

  // The risen, the bill's fourth figure: same sites again.
  const battleRaised = Object.fromEntries(
    Object.entries(metadata.battle_raised || {}).filter(([site]) => site in battleTotals)
  )

  // What a player knows is theirs (owner, 2026-09-09), mirroring the server's keep-list
  // (`PUBLIC_USER_FIELDS`): an enemy's entry says its side, its class, the cities it started on,
  // its stance badge and when it fell — never its food or its research.
  // Teammates keep everything; a partner's research is what their cities see with.
  const usersSeen = Object.fromEntries(
    Object.entries(users).map(([id, entry]) => [
      id,
      allies.has(String(id))
        ? entry
        : Object.fromEntries(Object.entries(entry || {}).filter(([key]) => PUBLIC_USER_FIELDS.includes(key))),
    ])
  )

  // The log that produced this board (owner, 2026-10-26, `requirements/36` P2), cut to the seat's
  // side with the sight of this board — `redactMotion`, the server's `redact_motion`.
  const { motion: rawMotion, ...board } = metadata
  const motion = rawMotion ? redactMotion(rawMotion, { team: viewer.team, users, seen: seenSquares }) : null
  const redacted = {
    ...board,
    ...(motion ? { motion } : {}),
    users: usersSeen,
    cells,
    movements,
    events,
    formations,
    strikes,
    upgrades,
    foundings,
    tunnel_orders: tunnelOrders,
    discharges,
    battle_totals: battleTotals,
    battle_meat: battleMeat,
    battle_revived: battleRevived,
    battle_raised: battleRaised,
  }
  return { ...redacted, units_per_user: census(redacted) }
}
